import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { cx } from "../lib/cx";
import { useI18n } from "../i18n";
import { formatMoney } from "../lib/format";
import { usePrefersReducedMotion } from "../lib/hooks";

/** ภาพตัวอย่างบนหน้า Landing: ภาพถ่ายจริง + SVG mock ของเส้นขอบเขต กริด และป้ายขนาด (ยังไม่ใช่ผลคำนวณจริง) */
const STEP_DELAY = 1300;

export function DemoStage() {
  const { t } = useI18n();
  const reduced = usePrefersReducedMotion();
  const [step, setStep] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  const clear = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const play = useCallback(() => {
    clear();
    if (reduced) {
      setStep(3);
      return;
    }
    setStep(0);
    [1, 2, 3].forEach((n, i) => timers.current.push(window.setTimeout(() => setStep(n), 500 + i * STEP_DELAY)));
  }, [reduced]);

  useEffect(() => {
    const el = root.current;
    if (!el) return;
    if (typeof IntersectionObserver !== "function") {
      play();
      return clear;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          play();
          io.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clear();
    };
  }, [play]);

  const layer = (n: number) => ({ opacity: step >= n ? 1 : 0, transition: "opacity var(--dur-slow) var(--ease-enter)" });
  const label = { fill: "#fff", fontWeight: 700, fontSize: 13, paintOrder: "stroke", stroke: "rgba(17,17,17,.75)", strokeWidth: 3.5, strokeLinejoin: "round" } as const;

  return (
    <div className="demo" aria-labelledby="demoT" ref={root}>
      <div className="row-between" style={{ marginBottom: 8 }}>
        <h2 id="demoT" style={{ fontSize: "var(--fs-md)" }}>
          {t("home.demo.title")}
        </h2>
        <Button variant="ghost" size="sm" onClick={play}>
          <Icon name="refresh" size="sm" />
          {t("home.demo.replay")}
        </Button>
      </div>
      <div className="photo-stage" style={{ aspectRatio: "4 / 3.4" }}>
        <img src="/assets/site-ceiling-750.jpg" srcSet="/assets/site-ceiling-480.jpg 480w, /assets/site-ceiling-640.jpg 640w, /assets/site-ceiling-750.jpg 750w" sizes="(min-width: 1024px) 560px, calc(100vw - 32px)" width={750} height={637} decoding="async" alt={t("web.home.demo.alt")} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 30%" }} />
        <svg className="ov" viewBox="0 0 400 340" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
          <g style={layer(1)}>
            <polygon points="0,52 400,6 400,150 228,172 150,150 0,172" fill="var(--ov-boundary)" fillOpacity="0.12" />
            <path d="M0,172 L150,150 L228,172 L400,150 M0,52 L400,6" stroke="var(--ov-boundary)" strokeWidth="3.5" fill="none" vectorEffect="non-scaling-stroke" />
            <path d="M150,150 L150,214 M150,214 L0,236" stroke="var(--ov-existing)" strokeWidth="2.5" strokeDasharray="7 5" fill="none" vectorEffect="non-scaling-stroke" />
          </g>
          <g style={layer(2)} stroke="var(--ov-grid)" strokeWidth="1.2" fill="none" vectorEffect="non-scaling-stroke">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <path key={`a${i}`} d={`M${i * 67},${48 - i * 7} L${70 + i * 52},${168 - i * 3}`} vectorEffect="non-scaling-stroke" />
            ))}
            {[1, 2, 3, 4].map((i) => (
              <path key={`b${i}`} d={`M0,${52 + i * 29} L400,${6 + i * 36}`} vectorEffect="non-scaling-stroke" />
            ))}
          </g>
          <g style={layer(3)}>
            <text x="200" y="48" textAnchor="middle" style={label}>
              {t("web.home.demo.label.height")}
            </text>
            <text x="70" y="232" textAnchor="middle" style={label}>
              {t("web.home.demo.label.depth")}
            </text>
            <text x="70" y="200" textAnchor="middle" style={label}>
              {t("web.home.demo.label.width")}
            </text>
            <text x="268" y="150" textAnchor="middle" style={label}>
              {t("web.home.demo.label.room")}
            </text>
          </g>
        </svg>
        <div className={cx("demo-price", step >= 3 && "show")} aria-live="polite">
          <div>
            <div className="xs muted">{t("home.demo.price")}</div>
            <div className="small">{t("home.demo.job")}</div>
          </div>
          <div className="v">{formatMoney(13123)}</div>
        </div>
      </div>
      <div className="demo-steps">
        {(["home.demo.step1", "home.demo.step2", "home.demo.step3"] as const).map((k, i) => (
          <span key={k} className={cx(i < step && "on")}>
            <b>{i + 1}</b>
            {t(k)}
          </span>
        ))}
      </div>
    </div>
  );
}
